import {
  Breadcrumb,
  Button,
  Input,
  message,
  PageHeader,
  Select,
  Form,
  Row,
  Col,
  Popconfirm,
  Drawer,
  Tag,
  Table,
  Spin,
  DatePicker,
  Modal,
} from "antd";

import { EditOutlined, DownloadOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Page from "_components/Page";
import {
  documentService,
  measurementTypeService,
  packingTypeService,
  stockClassificationService,
  ApplicationDocumentService,
  userService,
} from "_services";
import TabMenu from "./TabMenu";
import { DocumentType, Utils } from "_helpers";

function SubDocuments({ history, match }) {
  const { id, status } = match.params;

  const { path } = match;

  const [isLoading, setIsLoading] = useState(false);
  const [stockType, setStockType] = useState({});
  const [stockTypeMode, setStockTypeMode] = useState("OPEN");
  const [packingTypes, setPackingTypes] = useState([]);
  const [stockClasses, setStockClasses] = useState([]);
  //const [qualityTypes, setQualityTypes] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [measurementTypes, setMeasurementTypes] = useState([]);
  const [materialLines, setMaterialLines] = useState([]);
  const [form] = Form.useForm();
  const [companyDocuments, setCompanyDocuments] = useState([]);
  const [editDrawerVisible, setEditDrawerVisible] = useState(false);
  const [loadingAddEditStockItem, setLoadingAddEditStockItem] = useState(false);
  const [base64File, setBase64File] = useState(null);
  const [originalFileName, setOriginalFileName] = useState(null);

  const [addEditStockItemForm] = Form.useForm();
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [approvalModalVisible, setApprovalModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [approvalForm] = Form.useForm();
  const [users, setUsers] = useState([]);
  const { Option } = Select;

  // Load company sub-documents table and users
  useEffect(() => {
    setIsLoading(true);
    
    // Fetch users
    userService.getAll()
      .then((response) => {
        setUsers(response.content || []);
      })
      .catch((error) => {
        console.error("Error fetching users:", error);
      });
    
    // First get the application document by ID to retrieve the company ID
    ApplicationDocumentService
      .getById(id)
      .then((response) => {
        const documentData = response.content;
        // Then fetch sub-documents using the company ID from the document
        if (documentData?.company?.id) {
          ApplicationDocumentService
            .getSubDocumentByCompanyId(documentData.company.id)
            .then((response) => {
              const documents = response.content || [];
              setCompanyDocuments(Array.isArray(documents) ? documents : [documents]);
              setIsLoading(false);
            })
            .catch((error) => {
              setIsLoading(false);
              setCompanyDocuments([]);
              message.error(`Failed to load company documents: ${error}`);
            });
        } else {
          setIsLoading(false);
          setCompanyDocuments([]);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setCompanyDocuments([]);
        message.error(`Failed to load document: ${error}`);
      });
  }, [id]);

  const handleDownloadSubDocument = (record) => {
    if (!record?.id) return;
    ApplicationDocumentService.downloadSubDocument(record.id)
      .then((response) => {
        const fileNameFromPath = (record.documentLocation || "").split("/").pop();
        const safeName = record.documentName || fileNameFromPath || `document_${record.id}`;
        Utils.downloadFileFromBlob(response, safeName);
      })
      .catch(() => {
        message.error("Download failed");
      });
  };

  const handleStatusChange = (record, newStatus) => {
    setSelectedRecord(record);
    setSelectedStatus(newStatus);
    setApprovalModalVisible(true);
    // Pre-fill the form with existing comment if any
    approvalForm.setFieldsValue({
      comment: record.comment || '',
      approvedBy: record.approvedBy || ''
    });
  };

  const submitStatusChange = () => {
    approvalForm.validateFields().then((values) => {
      setUpdatingStatus(prev => ({ ...prev, [selectedRecord.id]: true }));
      
      const payload = {
        ...selectedRecord,
        applicationStatus: selectedStatus,
        comment: values.comment,
        approvedBy: values.approvedBy,
        approvedDate: new Date().toISOString()
      };

      ApplicationDocumentService.update(selectedRecord.id, payload)
        .then((response) => {
          message.success(`Document status updated to ${selectedStatus}`);
          setApprovalModalVisible(false);
          approvalForm.resetFields();
          
          // Refresh the documents list
          ApplicationDocumentService.getById(id)
            .then((response) => {
              const documentData = response.content;
              if (documentData?.company?.id) {
                ApplicationDocumentService.getSubDocumentByCompanyId(documentData.company.id)
                  .then((response) => {
                    const documents = response.content || [];
                    setCompanyDocuments(Array.isArray(documents) ? documents : [documents]);
                  })
                  .catch((error) => {
                    console.error("Error refreshing documents:", error);
                  });
              }
            });
        })
        .catch((error) => {
          message.error(`Failed to update status: ${error.message || error}`);
        })
        .finally(() => {
          setUpdatingStatus(prev => ({ ...prev, [selectedRecord.id]: false }));
        });
    }).catch((error) => {
      console.log("Validation failed:", error);
    });
  };

  const handleCancelApproval = () => {
    setApprovalModalVisible(false);
    approvalForm.resetFields();
    setSelectedRecord(null);
    setSelectedStatus(null);
  };

  const handleStatusUpdate = () => {
    form.validateFields().then((values) => {
      setIsLoading(true);

      const payload = {
        linkType: values.linkType,
        applicationDocumentType: values.documentType,
        base64FileString: base64File,
        originalFileName: originalFileName,
        validFrom: values.validFrom
          ? values.validFrom.format("YYYY-MM-DD")
          : null,
        validTo: values.validTo ? values.validTo.format("YYYY-MM-DD") : null,
        comment: values.comment,
        //when adding sub documents to main document
        parentApplication: id,
      };

      documentService
        .update(id, payload)
        .then((x) => {
          setIsLoading(false);
          message.success("Successfully Updated!", 3);
          history.push("/documents");
        })
        .catch((errorInfo) => {
          setIsLoading(false);
          message.error(`${errorInfo}`);
        });
    });
  };

  const handleLineEdit = (record) => {
    addEditStockItemForm.setFieldsValue(record);

    setEditDrawerVisible(true);
  };

  const getLineColumns = () => {
    let columns = [];

    columns.push({
      title: "HS Code",
      dataIndex: "materialType",
      key: "materialType",
      //width: "20%",
      render: (text, record) => {
        if (text) {
          return `${text.code}`;
        }
      },
    });
    columns.push({
      title: "Company Code",
      dataIndex: "materialType",
      key: "materialType",
      //width: "20%",
      render: (text, record) => {
        // if (text) {
        //   return `${text.secondaryCode}`;
        // }

        return (
          text?.secondaryCode.toString().substring(0, 16) +
          text?.secondaryCode.toString().slice(-5)
        );
      },
    });
    columns.push({
      title: "Description",
      dataIndex: "materialType",
      key: "materialType",
      //width: "20%",
      render: (text, record) => {
        if (text) {
          return `${text.name}`;
        }
      },
    });
    columns.push({
      title: "Package type",
      dataIndex: ["materialType", "packingType", "name"],
      key: "packageType",
    });

    columns.push({
      title: "Quantity Per Unit",
      dataIndex: "quantityPerUnit",
      key: "quantityPerUnit",
      align: "center",
    });
    columns.push({
      title: "Conversion Ratio",
      dataIndex: "conversionRatio",
      key: "conversionRatio",
      align: "center",
    });
    columns.push({
      title: "Wastage (%)",
      dataIndex: "wastagePercentage",
      align: "center",
      key: "wastagePercentage",
    });
    columns.push({
      title: "UOM",
      dataIndex: "uom",
      key: "uom",
      render: (text, record) => {
        if (text) {
          return text.name;
        }
      },
    });
    columns.push({
      title: "Available Quantity",
      dataIndex: "availableQuantity",
      align: "center",
      key: "availableQuantity",
      render: (text, record) => text ?? "0",
    });

    if (stockType?.mode == "UPDATE") {
      columns.push({
        title: "Action",
        key: "action",
        width: "6%",
        render: (text, record) => {
          return (
            //When need to check the line open or not ["OPEN"].includes(record.lineStage) &&
            <span>
              {
                <Button
                  icon={<EditOutlined />}
                  title="Edit"
                  type="link"
                  style={{ marginRight: 10 }}
                  onClick={() => handleLineEdit(record)}
                ></Button>
              }
              {/* {
              <Popconfirm
                title="Are you sure to delete this line?"
                onConfirm={() => handleLineDelete(record.id)}
              >
                <CloseOutlined
                  loading={loadingLineDelete && record.key === activeTabKey}
                />
              </Popconfirm>
            } */}
            </span>
          );
        },
      });
    }

    return columns;
  };

  const onFill = (data) => {
    form.setFieldsValue(data);
  };

  const onCancel = () => {
    history.push("/documents");
  };

  const onCloseAddEditStockItemDrawer = () => {
    setEditDrawerVisible(false);
  };

  const handleAddEditStockItem = (item) => {
    let currentItem = { ...stockType };

    addEditStockItemForm
      .validateFields()
      .then((values) => {
        setIsLoading(true);

        const modifiedList = currentItem.materials.map((material) => {
          if (material.id === values.id) {
            return {
              ...material,
              quantityPerUnit: values.quantityPerUnit,
              materialType: {
                ...material.materialType,
                code: values.materialType.code,
                secondaryCode: values.materialType.secondaryCode,
                name: values.materialType.name,
              },
            };
          }
          return material;
        });

        const modifieditem = { ...currentItem, materials: [...modifiedList] };

        setStockType(modifieditem);
        setIsLoading(false);
        setEditDrawerVisible(false);
      })
      .catch((errorInfo) => {
        console.log(errorInfo);
      });
  };

  const getActionButtons = () => {
    let buttons = [];
    // Add any additional action buttons here if needed
    return buttons;
  };

  const onFileChange = (e) => {
    if (e.target.files[0]) {
      setOriginalFileName(e.target.files[0].name);
      let reader = new FileReader();
      reader.onload = function (e) {
        setBase64File(reader.result);
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  return (
    <div>
      <PageHeader
        title={
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/dashboard">Dashboard</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to="/documents">Documents</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>Company Documents</Breadcrumb.Item>
          </Breadcrumb>
        }
        //Add buttons in the page header
        extra={getActionButtons()}
      >
        <Page inner>
          <Spin tip="Loading..." spinning={isLoading}>
             <TabMenu menu="sub-documents" id={id} />

            {false && (
            <Form
              form={form}
              name="form-create"
              layout="vertical"
              requiredMark={false}
            >
              {/* <Row gutter={24}>
                <Col span={8}>
                  <Form.Item
                    name="code"
                    label="HS Code"
                    rules={[{ required: true, message: "HS Code is required" }]}
                  >
                    <Input readOnly={true} placeholder="HS Code" />
                  </Form.Item>
                </Col> */}

              {/* <Col span={8}>
                  <Form.Item name="name" label="Name">
                    <Input readOnly={true} placeholder="Name" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="secondaryCode" label="Company Code">
                    <Input readOnly={true} placeholder="Company Code" />
                  </Form.Item>
                </Col> */}
              {/* </Row> */}
              <Row gutter={24}>
                <Col span={6}>
                  <Form.Item name="documentType" label="Document Type">
                    <Select placeholder="Please select" allowClear>
                      {DocumentType.map((age) => (
                        <Select.Option value={age.value} key={age.value}>
                          {age.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="file"
                    label="File upload"
                    rules={[
                      {
                        required: true,
                        message: "Valid Excel file is required",
                      },
                    ]}
                    valuePropName="file"
                  >
                    <Input
                      type="file"
                      name="file"
                      onChange={onFileChange}
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <Form.Item name={["validFrom"]} label="Valid From">
                    <DatePicker
                      placeholder="Valid From"
                      format="YYYY-MM-DD"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>

                <Col span={5}>
                  <Form.Item name={["validTo"]} label="Valid To">
                    <DatePicker
                      placeholder="Valid To"
                      format="YYYY-MM-DD"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item name={["comment"]} label="Comment">
                    <Input.TextArea placeholder="Comment" rows={4} />
                  </Form.Item>
                </Col>
              </Row>
              {/* <Col span={6}> */}
              {/* <Form.Item name={["qualityType", "id"]} label="Quality Type" rules={[
                  {
                    required: true,
                    message: "Quality Type is required",
                  },
                ]}>
                  <Select
                    showSearch
                    allowClear
                    showArrow={false}
                    optionFilterProp="children"
                    placeholder="Quality Type"
                  >
                    {qualityTypes.map((t) => (
                      <Select.Option value={t.id} key={t.id}>
                        {t.name} ({t.code})
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item> */}
              {/* </Col>
              <Col span={6}></Col> */}

              {/* {materialLines && (
                <Table
                  bordered
                  pagination={false}
                  columns={getLineColumns()}
                  dataSource={stockType.materials}
                  size="small"
                />
              )} */}
            </Form>
            )}

            {/* Company Documents Table */}
            <div style={{ marginTop: 16 }}>
              <Table
                size="small"
                bordered
                pagination={{ pageSize: 10, showSizeChanger: true }}
                dataSource={companyDocuments}
                rowKey="id"
                loading={isLoading}
                locale={{ emptyText: 'No documents found for this company' }}
                columns={[
                  {
                    title: 'Document Type',
                    dataIndex: 'applicationDocumentType',
                    key: 'applicationDocumentType',
                    width: '15%',
                    render: (text) => (
                      <span style={{ fontWeight: 'bold' }}>{text}</span>
                    ),
                  },
                  {
                    title: 'Document Name',
                    dataIndex: 'documentName',
                    key: 'documentName',
                    width: '12%',
                    render: (text) => (
                      <span style={{ fontWeight: 'bold' }}>{text}</span>
                    ),
                  },
                  {
                    title: 'Status',
                    dataIndex: 'applicationStatus',
                    key: 'applicationStatus',
                    width: '10%',
                    render: (applicationStatus) => {
                      let color = "";
                      if (applicationStatus === "APPROVAL") {
                        color = "green";
                      } else if (applicationStatus === "CLOSURE") {
                        color = "red";
                      } else if (applicationStatus === "APPLICATION") {
                        color = "blue";
                      } else if (applicationStatus === "EXTENSIONS") {
                        color = "purple";
                      } else if (applicationStatus === "AMENDMENTS") {
                        color = "orange";
                      } else {
                        color = "blue";
                      }
                      return <Tag color={color}>{applicationStatus || 'APPLICATION'}</Tag>;
                    },
                  },
                  {
                    title: 'Approved By',
                    key: 'approvedBy',
                    width: '10%',
                    render: (text, record) => {
                      if (record.approvedBy) {
                        return (
                          <div style={{ fontSize: '12px' }}>
                            <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
                              {record.approvedBy}
                            </div>
                            {record.approvedDate && (
                              <div style={{ color: '#8c8c8c', fontSize: '11px' }}>
                                {new Date(record.approvedDate).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        );
                      }
                      return <span style={{ color: '#bfbfbf' }}>-</span>;
                    },
                  },
                  {
                    title: 'Comment',
                    dataIndex: 'comment',
                    key: 'comment',
                    width: '15%',
                    ellipsis: true,
                    render: (text) => (
                      <span style={{ fontSize: '12px' }}>{text || '-'}</span>
                    ),
                  },
                  {
                    title: 'Approval In Progress',
                    key: 'approval',
                    width: '20%',
                    render: (text, record) => {
                      const isUpdating = updatingStatus[record.id];
                      return (
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            type={record.applicationStatus === 'APPROVAL' ? 'primary' : 'default'}
                            loading={isUpdating}
                            onClick={() => handleStatusChange(record, 'APPROVAL')}
                            style={{ 
                              fontSize: '11px',
                              padding: '0 8px',
                              backgroundColor: record.applicationStatus === 'APPROVAL' ? '#52c41a' : undefined,
                              borderColor: record.applicationStatus === 'APPROVAL' ? '#52c41a' : undefined
                            }}
                          >
                            APPROVAL
                          </Button>
                          <Button
                            size="small"
                            type={record.applicationStatus === 'AMENDMENTS' ? 'primary' : 'default'}
                            loading={isUpdating}
                            onClick={() => handleStatusChange(record, 'AMENDMENTS')}
                            style={{ 
                              fontSize: '11px',
                              padding: '0 8px',
                              backgroundColor: record.applicationStatus === 'AMENDMENTS' ? '#fa8c16' : undefined,
                              borderColor: record.applicationStatus === 'AMENDMENTS' ? '#fa8c16' : undefined
                            }}
                          >
                            AMENDMENTS
                          </Button>
                          <Button
                            size="small"
                            type={record.applicationStatus === 'EXTENSIONS' ? 'primary' : 'default'}
                            loading={isUpdating}
                            onClick={() => handleStatusChange(record, 'EXTENSIONS')}
                            style={{ 
                              fontSize: '11px',
                              padding: '0 8px',
                              backgroundColor: record.applicationStatus === 'EXTENSIONS' ? '#722ed1' : undefined,
                              borderColor: record.applicationStatus === 'EXTENSIONS' ? '#722ed1' : undefined
                            }}
                          >
                            EXTENSIONS
                          </Button>
                        </div>
                      );
                    },
                  },
                  {
                    title: 'Action',
                    key: 'action',
                    width: '8%',
                    render: (text, record) => (
                      <Button 
                        type="link" 
                        size="small" 
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownloadSubDocument(record)}
                      >
                        Download
                      </Button>
                    ),
                  },
                ]}
              />
            </div>
          </Spin>

          {/* Approval Modal */}
          <Modal
            title={`Change Status to ${selectedStatus}`}
            open={approvalModalVisible}
            onOk={submitStatusChange}
            onCancel={handleCancelApproval}
            confirmLoading={selectedRecord && updatingStatus[selectedRecord.id]}
            okText="Submit"
            cancelText="Cancel"
            width={500}
          >
            {/* Document Details - Read Only Display */}
            <div style={{ marginBottom: '24px', padding: '16px', border: '1px solid #d9d9d9', borderRadius: '6px' }}>
              <div style={{ marginBottom: '12px' }}>
                <strong>Document Type:</strong> 
                <span style={{ marginLeft: '8px', fontWeight: 'bold' }}>{selectedRecord?.applicationDocumentType}</span>
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <strong>Document Name:</strong> 
                <span style={{ marginLeft: '8px', fontWeight: 'bold' }}>{selectedRecord?.documentName}</span>
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <strong>Current Status:</strong> 
                <Tag 
                  color={
                    selectedRecord?.applicationStatus === "APPROVAL" ? "green" :
                    selectedRecord?.applicationStatus === "CLOSURE" ? "red" :
                    selectedRecord?.applicationStatus === "EXTENSIONS" ? "purple" :
                    selectedRecord?.applicationStatus === "AMENDMENTS" ? "orange" :
                    "blue"
                  }
                  style={{ marginLeft: '8px', fontSize: '12px', padding: '2px 8px' }}
                >
                  {selectedRecord?.applicationStatus || 'APPLICATION'}
                </Tag>
              </div>
              
              <div>
                <strong>New Status:</strong> 
                <Tag 
                  color={
                    selectedStatus === "APPROVAL" ? "green" :
                    selectedStatus === "CLOSURE" ? "red" :
                    selectedStatus === "EXTENSIONS" ? "purple" :
                    selectedStatus === "AMENDMENTS" ? "orange" :
                    "blue"
                  }
                  style={{ marginLeft: '8px', fontSize: '12px', padding: '2px 8px' }}
                >
                  {selectedStatus}
                </Tag>
              </div>
            </div>

            {/* Approval Form Fields */}
            <Form
              form={approvalForm}
              layout="vertical"
              name="approval-form"
            >
              <Form.Item
                name="approvedBy"
                label="Approved By"
                rules={[
                  { required: true, message: 'Please select an approver' }
                ]}
              >
                <Select
                  placeholder="Select Approver"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {users.map((user) => (
                    <Option value={user.username || user.userName} key={user.id}>
                      {user.username || user.userName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              
              <Form.Item
                name="comment"
                label="Comment"
                rules={[
                  { required: true, message: 'Please enter a comment' }
                ]}
              >
                <Input.TextArea 
                  rows={4} 
                  placeholder="Enter your comment here..."
                  maxLength={500}
                  showCount
                />
              </Form.Item>
            </Form>
          </Modal>

          <Drawer
            title={`Edit Material `}
            width={360}
            onClose={onCloseAddEditStockItemDrawer}
            open={editDrawerVisible}
            bodyStyle={{ paddingBottom: 80 }}
          >
            <Form
              name="addEdit-stock"
              layout="vertical"
              requiredMark={false}
              form={addEditStockItemForm}
            >
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item name={"id"}>
                    <Input hidden />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    name={["materialType", "code"]}
                    label="HS Code"
                    rules={[{ required: true, message: "HS Code is required" }]}
                  >
                    <Input placeholder="HS Code" />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    name={["materialType", "secondaryCode"]}
                    label="Customer Code"
                  >
                    <Input placeholder="Customer Code" />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    name={["materialType", "name"]}
                    label="Description"
                  >
                    <Input placeholder="Description" />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item name={"quantityPerUnit"} label="Quantity Per unit">
                    <Input placeholder="Quantity Per Unit" />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item name={["uom", "name"]} label="UOM">
                    <Input disabled placeholder="uom" />
                  </Form.Item>
                </Col>
                <Col span={6}></Col>
              </Row>
            </Form>
            <div
              style={{
                position: "absolute",
                right: 0,
                bottom: 0,
                width: "100%",
                borderTop: "1px solid #e9e9e9",
                padding: "10px 16px",
                background: "#fff",
                textAlign: "right",
              }}
            >
              <Button
                onClick={onCloseAddEditStockItemDrawer}
                style={{ marginRight: 8 }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddEditStockItem}
                type="primary"
                loading={loadingAddEditStockItem}
              >
                Submit
              </Button>
            </div>
          </Drawer>
        </Page>
      </PageHeader>
    </div>
  );
}

export default SubDocuments;
